import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import CandidateKiosk from './pages/CandidateKiosk';
import Shell from './components/Shell';
import Dashboard from './pages/Dashboard';
import GatePasses from './pages/GatePasses';
import Visitors from './pages/Visitors';
import ItemPasses from './pages/ItemPasses';
import Candidates from './pages/Candidates';
import MonthlyPasses from './pages/MonthlyPasses';
import Approvals from './pages/Approvals';
import Scanner from './pages/Scanner';
import Reports from './pages/Reports';
import VisitorGatePassReport from './pages/reports/VisitorGatePassReport';
import EmployeeGatePassReport from './pages/reports/EmployeeGatePassReport';
import ItemReport from './pages/reports/ItemReport';
import EmployeeMovementReport from './pages/reports/EmployeeMovementReport';
import CandidateReport from './pages/reports/CandidateReport';
import Admin from './pages/Admin';
import Notifications from './pages/Notifications';
import VisitorRequests from './pages/VisitorRequests';
import Settings from './pages/Settings';
import MyTeam from './pages/MyTeam';

function RequireAuth({ children, roles }) {
  const { user, loaded } = useAuth();
  const location = useLocation();
  if (!loaded) return <div className="h-screen flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/apply" element={<CandidateKiosk />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/gate-passes" element={<GatePasses />} />
        {/* Supervisors are redirected to the Dashboard; every other role's access is unchanged. */}
        <Route
          path="/visitors"
          element={
            <RequireAuth roles={['admin', 'hr', 'guard', 'employee']}>
              <Visitors />
            </RequireAuth>
          }
        />
        <Route
          path="/item-passes"
          element={
            <RequireAuth roles={['admin', 'hr', 'guard', 'employee']}>
              <ItemPasses />
            </RequireAuth>
          }
        />
        <Route
          path="/candidates"
          element={
            <RequireAuth roles={['hr']}>
              <Candidates />
            </RequireAuth>
          }
        />
        <Route
          path="/monthly-passes"
          element={
            <RequireAuth roles={['hr', 'employee', 'supervisor']}>
              <MonthlyPasses />
            </RequireAuth>
          }
        />
        <Route
          path="/my-team"
          element={
            <RequireAuth roles={['supervisor']}>
              <MyTeam />
            </RequireAuth>
          }
        />
        <Route
          path="/approvals"
          element={
            <RequireAuth roles={['admin', 'supervisor']}>
              <Approvals />
            </RequireAuth>
          }
        />
        <Route
          path="/scanner"
          element={
            <RequireAuth roles={['admin', 'guard']}>
              <Scanner />
            </RequireAuth>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth roles={['admin']}>
              <Reports />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/visitor-gate-passes"
          element={
            <RequireAuth roles={['admin']}>
              <VisitorGatePassReport />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/employee-gate-passes"
          element={
            <RequireAuth roles={['admin']}>
              <EmployeeGatePassReport />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/items"
          element={
            <RequireAuth roles={['admin']}>
              <ItemReport />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/employee-movement"
          element={
            <RequireAuth roles={['admin']}>
              <EmployeeMovementReport />
            </RequireAuth>
          }
        />
        <Route
          path="/reports/candidates"
          element={
            <RequireAuth roles={['admin']}>
              <CandidateReport />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth roles={['admin']}>
              <Admin />
            </RequireAuth>
          }
        />
        <Route
          path="/visitor-requests"
          element={
            <RequireAuth roles={['admin', 'hr', 'supervisor', 'employee']}>
              <VisitorRequests />
            </RequireAuth>
          }
        />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
